import { FC } from 'react';
import styled from 'styled-components';

import { TrackingStatusEnum } from 'order/wizard/orderTracking/enums/TrackingStatusEnum';
import {
  formatLocation,
  formatTimeWindow,
  formatTrackingDate,
  formatTrackingDay,
  formatWeight,
  getDelayLabel,
  getTotalWeight,
  getTrackingDelay,
  getTrackingStatus,
  isDelayed,
} from 'order/wizard/orderTracking/helpers/trackingHelpers';
import { TrackingDetails } from 'order/wizard/orderTracking/interface/OrderTracking';
import { ButtonPrimary, ButtonTertiaryLight } from 'shared/components/Button';
import { PSmall, PXSmall } from 'shared/components/Typography';
import { Wrapper } from 'shared/components/Wrapper';
import { nevada } from 'shared/config/Colors';

import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import { ReactComponent as ArrowRightIcon } from 'assets/icons/arrow-right.svg';
import { ReactComponent as SyncIcon } from 'assets/icons/sync.svg';

import { TrackingNumber } from './TrackingNumber';
import { TrackingStatusBadge } from './TrackingStatusBadge';

const PanelContainer = styled.div`
  background-color: ${({ theme }) => theme.athensGray};
  border: 1px solid ${({ theme }) => theme.mischka};
  border-radius: 12px;
  padding: 0 20px;
`;

const PanelSection = styled(Wrapper)`
  padding: 16px 0;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.mischka};
  }
`;

// Columns keep their own height, so a long shipment history doesn't stretch the info grid.
const Columns = styled.div`
  align-items: start;
  display: grid;
  gap: 32px;
  grid-template-columns: 1fr 1fr;
`;

const InfoGrid = styled.div`
  display: grid;
  gap: 16px 24px;
  grid-template-columns: 1fr 1fr;
`;

const Label = styled(PXSmall)`
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const FieldLabel = styled(Label)`
  margin-bottom: 4px;
`;

const RouteLine = styled.span`
  border-top: 1px solid ${({ theme }) => theme.silverSand};
  flex-grow: 1;
`;

const TimelineItem = styled.li`
  display: grid;
  gap: 12px;
  grid-template-columns: 56px 10px 1fr;
  list-style: none;
  padding-bottom: 14px;

  &:last-child {
    padding-bottom: 0;
  }
`;

const TimelineMarker = styled.span<{ latest: boolean }>`
  position: relative;

  &::before {
    background-color: ${({ theme, latest }) =>
      latest ? theme.kashmirBlue : theme.white};
    border: 1px solid
      ${({ theme, latest }) => (latest ? theme.kashmirBlue : theme.silverSand)};
    border-radius: 50%;
    content: '';
    height: 8px;
    left: 0;
    position: absolute;
    top: 4px;
    width: 8px;
  }

  ${TimelineItem}:not(:last-child) &::after {
    border-left: 1px solid ${({ theme }) => theme.silverSand};
    bottom: -18px;
    content: '';
    left: 4.5px;
    position: absolute;
    top: 14px;
  }
`;

const Dot = styled.span<{ active: boolean }>`
  background-color: ${({ theme, active }) =>
    active ? theme.kashmirBlue : theme.silverSand};
  border-radius: 50%;
  height: 6px;
  width: 6px;
`;

const SmallButtonCSS = `
  font-size: 12px;
  height: 28px;
  padding: 0 10px;
  gap: 6px;
`;

const PrevBoxButton = styled(ButtonTertiaryLight)`
  ${SmallButtonCSS}
`;

const NextBoxButton = styled(ButtonPrimary)`
  ${SmallButtonCSS}
`;

interface InfoFieldProps {
  label: string;
  value: string | null;
  note?: string | null;
}

const InfoField: FC<InfoFieldProps> = ({ label, value, note }) => (
  <Wrapper>
    <FieldLabel color={nevada}>{label}</FieldLabel>
    <PSmall>{value ?? '—'}</PSmall>
    {note && <PXSmall color={nevada}>{note}</PXSmall>}
  </Wrapper>
);

interface TrackingDetailsPanelProps {
  trackingDetails: TrackingDetails[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  totalWeightLabel: string;
}

export const TrackingDetailsPanel: FC<TrackingDetailsPanelProps> = ({
  trackingDetails,
  selectedIndex,
  onSelect,
  totalWeightLabel,
}) => {
  const box = trackingDetails[selectedIndex];
  const boxNumber = selectedIndex + 1;
  const boxCount = trackingDetails.length;
  const boxDelay = getTrackingDelay(box);

  const totalWeight = formatWeight(getTotalWeight(trackingDetails));
  const totalWeightNote =
    boxCount > 1 && totalWeight ? `${totalWeightLabel}: ${totalWeight}` : null;

  const shipped = formatTrackingDate(
    box.shippedOnUtc ?? box.pickedUpOnUtc,
    'MMM d, yyyy'
  );

  const lastChecked = formatTrackingDate(box.lastCheckedOnUtc, 'MMM d, h:mm a');

  const renderDeliveredInfo = () => (
    <InfoGrid>
      <InfoField
        label="Delivered"
        value={formatTrackingDate(box.deliveredOnUtc, 'MMM d, yyyy')}
        note={formatTrackingDate(box.deliveredOnUtc, 'h:mm a')}
      />
      <InfoField label="Service" value={box.serviceDescription} />
      <InfoField
        label="Received by"
        value={box.deliveryDetails?.receivedByName ?? null}
      />
      <InfoField
        label="Left at"
        value={box.deliveryDetails?.locationDescription ?? null}
      />
      <InfoField label="Shipped" value={shipped} />
      <InfoField
        label="Weight"
        value={formatWeight(box.weight)}
        note={totalWeightNote}
      />
    </InfoGrid>
  );

  const renderInTransitInfo = () => {
    const lastScan = formatLocation(box.lastScanLocation);

    return (
      <InfoGrid>
        <Wrapper style={{ gridColumn: '1 / -1' }}>
          <FieldLabel color={nevada}>Route</FieldLabel>
          <Wrapper flex middle gap={12}>
            <PSmall>{formatLocation(box.origin) ?? '—'}</PSmall>
            <RouteLine />
            <PSmall>{formatLocation(box.destination) ?? '—'}</PSmall>
          </Wrapper>
          {lastScan && <PXSmall color={nevada}>Last scan: {lastScan}</PXSmall>}
        </Wrapper>
        <InfoField
          label="Estimated delivery"
          value={formatTrackingDay(box.estimatedDeliveryOnUtc)}
          note={formatTimeWindow(box.estimatedDeliveryWindow)}
        />
        <InfoField label="Service" value={box.serviceDescription} />
        <InfoField label="Shipped" value={shipped} />
        <InfoField
          label="Weight"
          value={formatWeight(box.weight)}
          note={totalWeightNote}
        />
      </InfoGrid>
    );
  };

  return (
    <PanelContainer>
      <PanelSection flex middle gap={10} flexWrap>
        <Label color={nevada}>
          Showing box {boxNumber} of {boxCount}
        </Label>
        <TrackingNumber trackingNumber={box.trackingNumber} />
        <TrackingStatusBadge status={getTrackingStatus(box)} />
        {boxDelay && (
          <TrackingStatusBadge
            status={TrackingStatusEnum.DELAYED}
            label={getDelayLabel(boxDelay)}
          />
        )}
      </PanelSection>

      <PanelSection>
        {box.error ? (
          <PSmall color={nevada}>{box.error.message}</PSmall>
        ) : (
          <Columns>
            {box.isDelivered ? renderDeliveredInfo() : renderInTransitInfo()}

            <Wrapper>
              <Wrapper flex between middle gap={12} marginBottom={12}>
                <Label color={nevada}>Shipment history · Box {boxNumber}</Label>
                {lastChecked && (
                  <Wrapper flex middle gap={4} color={nevada}>
                    <SyncIcon width={12} height={12} />
                    <PXSmall color={nevada}>
                      Checked with FedEx {lastChecked}
                    </PXSmall>
                  </Wrapper>
                )}
              </Wrapper>

              {!box.scanEvents.length && (
                <PSmall color={nevada}>No shipment history yet.</PSmall>
              )}

              <ul>
                {box.scanEvents.map((scanEvent, index) => (
                  <TimelineItem
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                  >
                    <Wrapper align="right">
                      <PXSmall bold>
                        {formatTrackingDate(scanEvent.occurredOnUtc, 'MMM d')}
                      </PXSmall>
                      <PXSmall color={nevada}>
                        {formatTrackingDate(scanEvent.occurredOnUtc, 'h:mm a')}
                      </PXSmall>
                    </Wrapper>

                    <TimelineMarker latest={index === 0} />

                    <Wrapper>
                      <PSmall bold>{scanEvent.description}</PSmall>
                      {scanEvent.exceptionDescription && (
                        <PXSmall color={nevada}>
                          {scanEvent.exceptionDescription}
                        </PXSmall>
                      )}
                      <PXSmall color={nevada}>
                        {formatLocation(scanEvent.location)}
                      </PXSmall>
                      {isDelayed(scanEvent.delay) && (
                        <TrackingStatusBadge
                          status={TrackingStatusEnum.DELAYED}
                          label={getDelayLabel(scanEvent.delay)}
                        />
                      )}
                    </Wrapper>
                  </TimelineItem>
                ))}
              </ul>
            </Wrapper>
          </Columns>
        )}
      </PanelSection>

      <PanelSection flex between middle>
        <Wrapper flex gap={6}>
          {trackingDetails.map((details, index) => (
            <Dot
              key={details.trackingNumber}
              active={index === selectedIndex}
            />
          ))}
        </Wrapper>

        <Wrapper flex middle gap={8}>
          <PXSmall color={nevada}>
            Box <strong>{boxNumber}</strong> of {boxCount}
          </PXSmall>
          <PrevBoxButton
            type="button"
            aria-label="Previous box"
            disabled={selectedIndex === 0}
            onClick={() => onSelect(selectedIndex - 1)}
          >
            <ArrowLeftIcon />
          </PrevBoxButton>
          <NextBoxButton
            type="button"
            disabled={boxNumber === boxCount}
            onClick={() => onSelect(selectedIndex + 1)}
          >
            Next box
            <ArrowRightIcon />
          </NextBoxButton>
        </Wrapper>
      </PanelSection>
    </PanelContainer>
  );
};
